/**
 * useChatTypingPresence
 *
 * Tracks remote typing presence for one chat with Supabase row-backed
 * realtime updates.
 *
 * Primary responsibilities:
 * - Upsert local typing start, stop, and heartbeat signals
 * - Keep a short-lived list of remote users who are actively typing
 * - Subscribe to `typing_indicators` row changes for the active chat
 * - Clear stale typing state on blur, visibility loss, and unmount
 *
 * Dependencies:
 * - lib/realtime/private-channel-auth
 * - lib/supabase/client
 * - lib/utils/safeConsole
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  RealtimePostgresChangesPayload
} from '@supabase/supabase-js'

import { syncPrivateRealtimeAuth } from '@/lib/realtime/private-channel-auth'
import {
  getRealtimeReconnectDelay,
  isRealtimeChannelFailure,
  removeRealtimeChannelOnce,
  shouldRemoveRealtimeChannel
} from '@/lib/realtime/channel-recovery'
import { createClient } from '@/lib/supabase/client'
import { Tables } from '@/supabase/types'
import { safeConsole } from '@/lib/utils/safeConsole'

const CHAT_TYPING_HEARTBEAT_MS = 2000
const CHAT_TYPING_STALE_AFTER_MS = 4000
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type BrowserSupabaseClient = ReturnType<typeof createClient>

interface ChatTypingRowPayload {
  chatId: string
  displayName: string
  isTyping: boolean
  sentAt: number
  userId: string
}

interface RemoteTypingState {
  displayName: string
  expiresAt: number
  lastSeenAt: number
}

interface UseChatTypingPresenceOptions {
  chatId: string | null
  displayName: string | null
  enabled?: boolean
  userId: string | null
}

export interface ChatTypingParticipant {
  displayName: string
  userId: string
}

function normalizeValue(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function normalizeChatId(value: string | null | undefined) {
  const normalized = normalizeValue(value)

  // `typing_indicators.chat_id` is a UUID-backed chat, not a draft UI key.
  return normalized && UUID_PATTERN.test(normalized) ? normalized : null
}

function canSubscribeToTypingRealtime() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

function getSortedRemoteTypers(
  remoteTypers: Map<string, RemoteTypingState>
): ChatTypingParticipant[] {
  return [...remoteTypers.entries()]
    .sort((left, right) => right[1].lastSeenAt - left[1].lastSeenAt)
    .map(([userId, value]) => ({
      displayName: value.displayName,
      userId
    }))
}

function getTypingTimestamp(lastUpdated: string | null | undefined) {
  const parsedTimestamp = lastUpdated ? Date.parse(lastUpdated) : Number.NaN

  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : Date.now()
}

async function writeTypingState(
  supabase: BrowserSupabaseClient,
  payload: ChatTypingRowPayload
) {
  const { error } = await supabase.from('typing_indicators').upsert(
    {
      chat_id: payload.chatId,
      user_id: payload.userId,
      user_name: payload.displayName,
      is_typing: payload.isTyping,
      last_updated: new Date(payload.sentAt).toISOString()
    },
    {
      onConflict: 'chat_id,user_id'
    }
  )

  if (error) {
    throw new Error(error.message)
  }
}

// --------------

export function useChatTypingPresence({
  chatId,
  displayName,
  enabled = true,
  userId
}: UseChatTypingPresenceOptions) {
  const normalizedChatId = normalizeChatId(chatId)
  const normalizedDisplayName = normalizeValue(displayName) ?? 'Someone'
  const normalizedUserId = normalizeValue(userId)

  const [remoteTypers, setRemoteTypers] = useState<ChatTypingParticipant[]>([])
  const [canSubscribeToRealtime, setCanSubscribeToRealtime] = useState(
    canSubscribeToTypingRealtime
  )
  const [reconnectNonce, setReconnectNonce] = useState(0)

  const clientRef = useRef<BrowserSupabaseClient | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const localTypingRef = useRef(false)
  const pruneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const remoteTypersRef = useRef<Map<string, RemoteTypingState>>(new Map())
  const isTypingChannelReadyRef = useRef(false)
  const lastSyncedAtRef = useRef(0)
  const syncHeartbeatRef = useRef<(() => void) | null>(null)

  const clearPruneTimer = useCallback(() => {
    if (!pruneTimeoutRef.current) {
      return
    }

    clearTimeout(pruneTimeoutRef.current)
    pruneTimeoutRef.current = null
  }, [])

  const clearHeartbeatTimer = useCallback(() => {
    if (!heartbeatRef.current) {
      return
    }

    clearInterval(heartbeatRef.current)
    heartbeatRef.current = null
  }, [])

  const startHeartbeatTimer = useCallback(() => {
    if (heartbeatRef.current) {
      return
    }

    heartbeatRef.current = setInterval(() => {
      if (!localTypingRef.current) {
        clearHeartbeatTimer()
        return
      }

      syncHeartbeatRef.current?.()
    }, CHAT_TYPING_HEARTBEAT_MS)
  }, [clearHeartbeatTimer])

  const syncRemoteTypers = useCallback(() => {
    clearPruneTimer()

    const now = Date.now()
    let nextExpiryAt: number | null = null

    for (const [remoteUserId, typingState] of remoteTypersRef.current.entries()) {
      if (typingState.expiresAt <= now) {
        remoteTypersRef.current.delete(remoteUserId)
        continue
      }

      if (nextExpiryAt === null || typingState.expiresAt < nextExpiryAt) {
        nextExpiryAt = typingState.expiresAt
      }
    }

    setRemoteTypers(getSortedRemoteTypers(remoteTypersRef.current))

    if (nextExpiryAt === null) {
      return
    }

    pruneTimeoutRef.current = setTimeout(() => {
      pruneTimeoutRef.current = null
      syncRemoteTypers()
    }, Math.max(nextExpiryAt - now, 0))
  }, [clearPruneTimer])

  const applyRemoteTypingRow = useCallback(
    (row: Tables<'typing_indicators'>) => {
      if (row.user_id === normalizedUserId) {
        return
      }

      if (!row.is_typing) {
        remoteTypersRef.current.delete(row.user_id)
        syncRemoteTypers()
        return
      }

      const lastSeenAt = getTypingTimestamp(row.last_updated)
      const expiresAt = lastSeenAt + CHAT_TYPING_STALE_AFTER_MS

      if (expiresAt <= Date.now()) {
        remoteTypersRef.current.delete(row.user_id)
        syncRemoteTypers()
        return
      }

      remoteTypersRef.current.set(row.user_id, {
        displayName: normalizeValue(row.user_name) ?? 'Someone',
        expiresAt,
        lastSeenAt
      })

      syncRemoteTypers()
    },
    [normalizedUserId, syncRemoteTypers]
  )

  const setLocalTyping = useCallback(
    async (nextIsTyping: boolean, options?: { force?: boolean }) => {
      const previousIsTyping = localTypingRef.current
      localTypingRef.current = nextIsTyping

      if (
        nextIsTyping &&
        canSubscribeToRealtime &&
        enabled &&
        normalizedChatId &&
        normalizedUserId
      ) {
        startHeartbeatTimer()
      } else if (!nextIsTyping) {
        clearHeartbeatTimer()
      }

      const supabase = clientRef.current

      if (
        !enabled ||
        !supabase ||
        !isTypingChannelReadyRef.current ||
        !normalizedChatId ||
        !normalizedUserId
      ) {
        return
      }

      const now = Date.now()
      const shouldWrite =
        options?.force === true ||
        nextIsTyping !== previousIsTyping ||
        (nextIsTyping && now - lastSyncedAtRef.current >= CHAT_TYPING_HEARTBEAT_MS)

      if (!shouldWrite) {
        return
      }

      lastSyncedAtRef.current = now

      try {
        await writeTypingState(supabase, {
          chatId: normalizedChatId,
          displayName: normalizedDisplayName,
          isTyping: nextIsTyping,
          sentAt: now,
          userId: normalizedUserId
        })
      } catch (error) {
        safeConsole.warn('Failed to sync chat typing state', error)
      }
    },
    [
      canSubscribeToRealtime,
      clearHeartbeatTimer,
      enabled,
      normalizedChatId,
      normalizedDisplayName,
      normalizedUserId,
      startHeartbeatTimer
    ]
  )

  useEffect(() => {
    syncHeartbeatRef.current = () => {
      void setLocalTyping(true, { force: true })
    }

    return () => {
      syncHeartbeatRef.current = null
    }
  }, [setLocalTyping])

  useEffect(() => {
    const stopLocalTyping = () => {
      if (!localTypingRef.current) {
        return
      }

      void setLocalTyping(false, { force: true })
    }

    const handleVisibilityChange = () => {
      const isVisible = canSubscribeToTypingRealtime()

      if (!isVisible) {
        stopLocalTyping()
      }

      setCanSubscribeToRealtime(isVisible)
    }

    window.addEventListener('pagehide', stopLocalTyping)
    window.addEventListener('blur', stopLocalTyping)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('pagehide', stopLocalTyping)
      window.removeEventListener('blur', stopLocalTyping)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [setLocalTyping])

  useEffect(() => {
    if (!enabled || !normalizedChatId || !normalizedUserId) {
      clientRef.current = null
      isTypingChannelReadyRef.current = false
      lastSyncedAtRef.current = 0
      localTypingRef.current = false
      remoteTypersRef.current.clear()
      setRemoteTypers([])
      clearHeartbeatTimer()
      clearPruneTimer()
      return
    }

    if (!canSubscribeToRealtime) {
      clientRef.current = null
      isTypingChannelReadyRef.current = false
      clearHeartbeatTimer()
      clearPruneTimer()
      return
    }

    lastSyncedAtRef.current = 0
    remoteTypersRef.current.clear()
    setRemoteTypers([])
    clearPruneTimer()

    const supabase = createClient()
    clientRef.current = supabase
    let isDisposed = false

    const clearReconnectTimer = () => {
      if (!reconnectTimeoutRef.current) {
        return
      }

      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    const scheduleReconnect = () => {
      if (isDisposed || reconnectTimeoutRef.current) {
        return
      }

      const delayMs = getRealtimeReconnectDelay(reconnectAttemptRef.current)
      reconnectAttemptRef.current += 1
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null

        if (!isDisposed) {
          setReconnectNonce(value => value + 1)
        }
      }, delayMs)
    }

    const channel = supabase.channel(`chat-typing:${normalizedChatId}`, {
      config: {
        private: true
      }
    })

    const loadInitialRemoteTypers = async () => {
      try {
        const { data, error } = await supabase
          .from('typing_indicators')
          .select('*')
          .eq('chat_id', normalizedChatId)
          .eq('is_typing', true)
          .gt(
            'last_updated',
            new Date(Date.now() - CHAT_TYPING_STALE_AFTER_MS).toISOString()
          )

        if (error) {
          throw new Error(error.message)
        }

        remoteTypersRef.current.clear()

        for (const row of data ?? []) {
          applyRemoteTypingRow(row)
        }

        syncRemoteTypers()
      } catch (error) {
        safeConsole.warn('Failed to load chat typing state', error)
      }
    }

    const handleRemoteTyping = (
      payload: RealtimePostgresChangesPayload<Tables<'typing_indicators'>>
    ) => {
      if (!payload) {
        return
      }

      if (payload.eventType === 'DELETE') {
        if (payload.old.user_id && payload.old.user_id !== normalizedUserId) {
          remoteTypersRef.current.delete(payload.old.user_id)
          syncRemoteTypers()
        }

        return
      }

      if (payload.new.chat_id !== normalizedChatId) {
        return
      }

      applyRemoteTypingRow(payload.new as Tables<'typing_indicators'>)
    }

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'typing_indicators',
        filter: `chat_id=eq.${normalizedChatId}`
      },
      handleRemoteTyping
    )

    void syncPrivateRealtimeAuth(supabase)
      .then((isReady) => {
        if (!isReady || isDisposed) {
          if (!isReady && !isDisposed) {
            void loadInitialRemoteTypers()
            scheduleReconnect()
          }

          return
        }

        channel.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            reconnectAttemptRef.current = 0
            clearReconnectTimer()
            isTypingChannelReadyRef.current = true
            void loadInitialRemoteTypers()

            if (localTypingRef.current) {
              void setLocalTyping(true, { force: true })
            }

            return
          }

          if (isRealtimeChannelFailure(status)) {
            isTypingChannelReadyRef.current = false

            if (!isDisposed) {
              safeConsole.warn('Chat typing channel failed', {
                chatId: normalizedChatId,
                status
              })
              clientRef.current = null
              void loadInitialRemoteTypers()
              if (shouldRemoveRealtimeChannel(status)) {
                void removeRealtimeChannelOnce(supabase, channel)
              }
              scheduleReconnect()
            }
          }
        })
      })
      .catch((error) => {
        if (!isDisposed) {
          safeConsole.warn('Chat typing realtime auth sync failed', error)
          void loadInitialRemoteTypers()
          scheduleReconnect()
        }
      })

    return () => {
      isDisposed = true

      clearHeartbeatTimer()
      clearReconnectTimer()
      clearPruneTimer()

      const shouldSendStop =
        localTypingRef.current && isTypingChannelReadyRef.current
      localTypingRef.current = false
      isTypingChannelReadyRef.current = false
      lastSyncedAtRef.current = 0
      remoteTypersRef.current.clear()

      if (shouldSendStop) {
        void writeTypingState(supabase, {
          chatId: normalizedChatId,
          displayName: normalizedDisplayName,
          isTyping: false,
          sentAt: Date.now(),
          userId: normalizedUserId
        }).catch((error) => {
          safeConsole.warn('Failed to clear chat typing state during cleanup', error)
        })
      }

      clientRef.current = null
      void removeRealtimeChannelOnce(supabase, channel)
    }
  }, [
    applyRemoteTypingRow,
    canSubscribeToRealtime,
    clearHeartbeatTimer,
    clearPruneTimer,
    enabled,
    normalizedChatId,
    normalizedDisplayName,
    normalizedUserId,
    reconnectNonce,
    setLocalTyping,
    syncRemoteTypers
  ])

  return {
    remoteTypers,
    setLocalTyping
  }
}
